import React, { useEffect, useState, useRef } from 'react';
import api from '../api';
import { 
    Search, CheckCircle2, XCircle, 
    UserCheck, AlertTriangle, PenTool, 
    Trash2, GraduationCap, Building2, FileWarning
} from 'lucide-react';

const THEME = {
    card: '#1e293b', border: '#334155', textSec: '#94a3b8', 
    primary: '#3b82f6', success: '#10b981', danger: '#ef4444', warning: '#facc15',
    bg: '#0f172a'
};

const Inductions = () => {
    const [people, setPeople] = useState([]);
    const [loading, setLoading] = useState(true);
    
    // Filters & Responsive
    const [search, setSearch] = useState('');
    const [filterCompany, setFilterCompany] = useState('');
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    // Modal State
    const [editingPerson, setEditingPerson] = useState(null);
    const [newDate, setNewDate] = useState('');
    
    // Signature
    const canvasRef = useRef(null);
    const [isDrawing, setIsDrawing] = useState(false);
    const [hasSigned, setHasSigned] = useState(false);
    const [viewingSignature, setViewingSignature] = useState(null);

    useEffect(() => { 
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        fetchInductions(); 
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const fetchInductions = async () => {
        try {
            const res = await api.get('inductions/');
            setPeople(res.data);
            setLoading(false);
        } catch (err) { console.error(err); setLoading(false); }
    };

    // --- ROBUST URL HELPER ---
    const getSignatureURL = (sig) => {
        if (!sig) return null;
        if (sig.startsWith('data:image')) return sig; // Base64
        
        // 1. Force HTTPS on Azure (Fixes "Mixed Content" broken images)
        if (sig.startsWith('http:') && window.location.protocol === 'https:') {
             return sig.replace('http:', 'https:');
        }
        
        // 2. Handle Relative Paths (Localhost vs Prod)
        if (!sig.startsWith('http')) {
            // If we are on localhost, assume backend is on port 8000
            if (window.location.hostname === 'localhost') {
                return `http://localhost:8000${sig.startsWith('/') ? '' : '/'}${sig}`;
            }
            // On Prod, if it's relative, just return it (browser handles domain)
            return sig;
        }

        return sig;
    };

    // Signature Logic
    const startDrawing = (e) => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        const rect = canvas.getBoundingClientRect();
        const clientX = e.touches ? e.touches[0].clientX : e.clientX;
        const clientY = e.touches ? e.touches[0].clientY : e.clientY;
        ctx.beginPath();
        ctx.moveTo(clientX - rect.left, clientY - rect.top);
        setIsDrawing(true);
        setHasSigned(true);
    };
    const draw = (e) => {
        if (!isDrawing) return;
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        const rect = canvas.getBoundingClientRect();
        const clientX = e.touches ? e.touches[0].clientX : e.clientX;
        const clientY = e.touches ? e.touches[0].clientY : e.clientY;
        ctx.lineTo(clientX - rect.left, clientY - rect.top);
        ctx.strokeStyle = '#000'; ctx.lineWidth = 2; ctx.stroke();
    };
    const stopDrawing = () => { setIsDrawing(false); };
    const clearSignature = () => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        setHasSigned(false);
    };

    const handleUpdate = async () => {
        if (!editingPerson) return;
        let signatureData = null;
        if (newDate && hasSigned && canvasRef.current) signatureData = canvasRef.current.toDataURL('image/png');
        
        try {
            await api.post('inductions/', {
                db_type: editingPerson.db_type, 
                id: editingPerson.id,
                date: newDate || null, 
                signature: signatureData 
            });
            alert("Induction Updated Successfully");
            setEditingPerson(null); 
            setHasSigned(false); 
            fetchInductions(); 
        } catch (err) { alert("Update failed"); }
    };

    const filteredPeople = people.filter(p => {
        const matchesSearch = p.name.toLowerCase().includes(search.toLowerCase()) || p.company.toLowerCase().includes(search.toLowerCase());
        const matchesCompany = filterCompany ? p.company === filterCompany : true;
        return matchesSearch && matchesCompany;
    });

    const companies = [...new Set(people.map(p => p.company))];
    const inputStyle = { padding:'10px', borderRadius:'6px', border:`1px solid ${THEME.border}`, background:'#020617', color:'white', minWidth: isMobile ? '100%' : '200px' };

    if (loading) return <div style={{color:'white'}}>Loading Registry...</div>;

    return (
        <div className="animate-fade-in">
            {/* HEADER & FILTERS */}
            <div style={{background: THEME.card, padding:'20px', borderRadius:'12px', border:`1px solid ${THEME.border}`, marginBottom:'20px'}}>
                <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'20px', flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? '10px' : '0', alignItems: isMobile ? 'flex-start' : 'center'}}>
                    <div style={{display:'flex', alignItems:'center', gap:'12px'}}>
                        <GraduationCap size={28} color={THEME.primary} />
                        <h2 style={{color: 'white', margin:0}}>Induction Registry</h2>
                    </div>
                    <div style={{display:'flex', gap:'15px', color: THEME.textSec, fontSize:'0.9rem', flexWrap:'wrap'}}>
                        <span>Total: <strong style={{color:'white'}}>{people.length}</strong></span>
                        <span style={{color: THEME.success, display:'flex', alignItems:'center', gap:'4px'}}><CheckCircle2 size={14}/> {people.filter(p => p.is_inducted).length}</span>
                        <span style={{color: THEME.danger, display:'flex', alignItems:'center', gap:'4px'}}><AlertTriangle size={14}/> {people.filter(p => !p.is_inducted).length}</span>
                    </div>
                </div>
                
                <div style={{display:'flex', gap:'15px', flexWrap:'wrap', flexDirection: isMobile ? 'column' : 'row'}}>
                    <div style={{position:'relative', width: isMobile ? '100%' : 'auto'}}>
                        <Search size={16} style={{position:'absolute', left:'10px', top:'12px', color:THEME.textSec}}/>
                        <input type="text" placeholder="Search Name..." value={search} onChange={e => setSearch(e.target.value)} style={{...inputStyle, paddingLeft:'35px'}} />
                    </div>
                    <div style={{position:'relative', width: isMobile ? '100%' : 'auto'}}>
                        <Building2 size={16} style={{position:'absolute', left:'10px', top:'12px', color:THEME.textSec}}/>
                        <select value={filterCompany} onChange={e => setFilterCompany(e.target.value)} style={{...inputStyle, paddingLeft:'35px'}}>
                            <option value="">All Companies</option>
                            {companies.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                    </div>
                </div>
            </div>

            {/* TABLE */}
            <div style={{background: THEME.card, borderRadius:'12px', border:`1px solid ${THEME.border}`, overflow:'hidden'}}>
                <div style={{overflowX: 'auto'}}>
                    <table style={{width: '100%', minWidth: '800px', textAlign: 'left', color: THEME.textSec, borderCollapse: 'collapse', fontSize:'0.9rem'}}>
                        <thead style={{background: '#020617', borderBottom: `1px solid ${THEME.border}`}}>
                            <tr>
                                <th style={{padding:'15px'}}>Name</th>
                                <th>Company</th>
                                <th>Role</th>
                                <th>Type</th>
                                <th>Status</th>
                                <th>Inducted By</th>
                                <th>Signature</th>
                                <th style={{textAlign:'right', paddingRight:'15px'}}>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredPeople.map((p, idx) => (
                                <tr key={`${p.db_type}-${p.id}`} style={{borderBottom: `1px solid ${THEME.border}`}}>
                                    <td style={{padding: '12px 15px', fontWeight:'bold', color:'white'}}>{p.name}</td>
                                    <td style={{color: THEME.warning}}>{p.company}</td>
                                    <td>{p.role}</td>
                                    <td><span style={{fontSize:'0.75rem', padding:'2px 8px', borderRadius:'4px', background: p.db_type === 'user' ? 'rgba(59, 130, 246, 0.2)' : 'rgba(168, 85, 247, 0.2)', color: p.db_type === 'user' ? THEME.primary : '#a855f7', border: `1px solid ${p.db_type === 'user' ? THEME.primary : '#a855f7'}`}}>{p.db_type === 'user' ? 'Account' : 'Worker'}</span></td>
                                    <td>
                                        {p.is_inducted ? 
                                            <span style={{color: THEME.success, fontWeight:'bold', background:'rgba(16, 185, 129, 0.1)', padding:'2px 8px', borderRadius:'4px', whiteSpace:'nowrap', display:'flex', alignItems:'center', gap:'4px', width:'fit-content'}}>
                                                <CheckCircle2 size={14}/> {p.induction_date}
                                            </span> : 
                                            <span style={{color: THEME.danger, fontWeight:'bold', background:'rgba(239, 68, 68, 0.1)', padding:'2px 8px', borderRadius:'4px', whiteSpace:'nowrap', display:'flex', alignItems:'center', gap:'4px', width:'fit-content'}}>
                                                <XCircle size={14}/> Pending
                                            </span>
                                        }
                                    </td>
                                    <td style={{color: p.inducted_by_name !== '-' ? THEME.primary : THEME.textSec}}>{p.inducted_by_name}</td>
                                    
                                    {/* --- SAFE SIGNATURE DISPLAY --- */}
                                    <td>
                                        {p.induction_signature ? (
                                            <SignatureThumbnail url={getSignatureURL(p.induction_signature)} />
                                        ) : '-'}
                                    </td>
                                    {/* ----------------------------- */}

                                    <td style={{textAlign:'right', paddingRight:'15px'}}>
                                        <button onClick={() => { setEditingPerson(p); setNewDate(p.induction_date || new Date().toISOString().split('T')[0]); }} style={{background:'transparent', border:`1px solid ${THEME.primary}`, color: THEME.primary, padding:'5px 12px', borderRadius:'6px', cursor:'pointer', fontWeight:'bold', display:'flex', alignItems:'center', gap:'6px', float:'right'}}>
                                            <PenTool size={14}/> Update
                                        </button>
                                    </td>
                                </tr>
                            ))}
                            {filteredPeople.length === 0 && <tr><td colSpan="8" style={{padding:'30px', textAlign:'center', color: THEME.textSec}}>No results found.</td></tr>}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* SIGNATURE PREVIEW */}
            {viewingSignature && (
                <div onClick={() => setViewingSignature(null)} style={{position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(0,0,0,0.85)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1100}}>
                    <div style={{background: 'white', padding: '15px', borderRadius: '12px', maxWidth: '90%'}} onClick={e => e.stopPropagation()}>
                        <img src={viewingSignature} style={{maxWidth: '100%', maxHeight: '300px', border: '1px solid #ccc', borderRadius:'4px'}} />
                        <button onClick={() => setViewingSignature(null)} style={{display:'block', width:'100%', padding:'12px', marginTop:'15px', background: THEME.danger, color:'white', border:'none', borderRadius:'6px', cursor:'pointer', fontWeight:'bold'}}>Close Preview</button>
                    </div>
                </div>
            )}

            {/* UPDATE MODAL */}
            {editingPerson && (
                <div style={{position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(0,0,0,0.8)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000}}>
                    <div style={{background: THEME.card, padding:'30px', borderRadius:'12px', border:`1px solid ${THEME.primary}`, width: isMobile ? '90%' : '400px', boxShadow:'0 0 40px rgba(0,0,0,0.5)'}}>
                        <h3 style={{marginTop:0, color:'white', display:'flex', alignItems:'center', gap:'10px'}}>
                            <UserCheck size={24} color={THEME.primary}/> Digital Induction
                        </h3>
                        <p style={{color: THEME.textSec, marginBottom:'20px'}}>Inducting <strong>{editingPerson.name}</strong> ({editingPerson.company})</p>
                        
                        <label style={{display:'block', color: THEME.textSec, marginBottom:'5px', fontSize:'0.9rem'}}>Date Completed</label>
                        <input type="date" value={newDate} onChange={e => setNewDate(e.target.value)} style={{width:'100%', padding:'10px', borderRadius:'6px', border:`1px solid ${THEME.border}`, background:'#020617', color:'white', marginBottom:'20px', boxSizing:'border-box'}} />

                        {newDate && (
                            <div style={{marginBottom:'20px'}}>
                                <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'5px'}}>
                                    <label style={{color: THEME.textSec, fontSize:'0.9rem'}}>Sign Below:</label>
                                    <button onClick={clearSignature} style={{fontSize:'0.8rem', background:'transparent', border:'none', color: THEME.danger, cursor:'pointer', display:'flex', alignItems:'center', gap:'4px'}}>
                                        <Trash2 size={12}/> Clear
                                    </button>
                                </div>
                                <div style={{background:'white', borderRadius:'6px', overflow:'hidden', border: `2px dashed ${THEME.border}`, touchAction: 'none'}}>
                                    <canvas ref={canvasRef} width={isMobile ? 300 : 340} height={150} style={{display:'block', cursor:'crosshair'}}
                                        onMouseDown={startDrawing} onMouseMove={draw} onMouseUp={stopDrawing} onMouseLeave={stopDrawing}
                                        onTouchStart={startDrawing} onTouchMove={draw} onTouchEnd={stopDrawing} />
                                </div>
                                {!hasSigned && <div style={{color: THEME.danger, fontSize:'0.8rem', marginTop:'5px', display:'flex', alignItems:'center', gap:'4px'}}><AlertTriangle size={12}/> Signature required to save.</div>}
                            </div>
                        )}

                        <div style={{display:'flex', gap:'10px'}}>
                            <button onClick={() => { setEditingPerson(null); setHasSigned(false); }} style={{flex:1, padding:'12px', background:'transparent', border:`1px solid ${THEME.border}`, color: THEME.textSec, borderRadius:'6px', cursor:'pointer'}}>Cancel</button>
                            <button onClick={handleUpdate} disabled={newDate && !hasSigned} style={{flex:1, padding:'12px', background: (newDate && !hasSigned) ? '#334155' : THEME.success, color:'white', border:'none', borderRadius:'6px', cursor: (newDate && !hasSigned) ? 'not-allowed' : 'pointer', fontWeight:'bold'}}>Confirm & Save</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

// --- NEW COMPONENT: SAFE THUMBNAIL (Prevents Crashes) ---
const SignatureThumbnail = ({ url }) => {
    const [error, setError] = useState(false);
    const [viewing, setViewing] = useState(false);

    if (error) {
        return (
            <div style={{width: '60px', height: '30px', background: 'rgba(255,255,255,0.1)', borderRadius: '4px', border: '1px dashed #64748b', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                <FileWarning size={14} color="#ef4444" />
            </div>
        );
    }

    return (
        <>
            <div 
                onClick={() => setViewing(true)} 
                style={{
                    width: '60px', height: '30px', background: 'white', 
                    borderRadius: '4px', cursor: 'pointer', overflow: 'hidden', 
                    border: '1px solid #334155', display: 'flex', alignItems: 'center', justifyContent: 'center'
                }}
            >
                <img 
                    src={url} 
                    alt="Sig" 
                    style={{maxHeight: '100%', maxWidth: '100%', objectFit: 'contain'}} 
                    onError={() => setError(true)}
                />
            </div>
            {viewing && (
                <div onClick={() => setViewing(false)} style={{position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(0,0,0,0.85)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1100}}>
                    <div style={{background: 'white', padding: '15px', borderRadius: '12px', maxWidth: '90%'}} onClick={e => e.stopPropagation()}>
                        <img src={url} style={{maxWidth: '100%', maxHeight: '300px', border: '1px solid #ccc', borderRadius:'4px'}} />
                        <button onClick={() => setViewing(false)} style={{display:'block', width:'100%', padding:'12px', marginTop:'15px', background: '#ef4444', color:'white', border:'none', borderRadius:'6px', cursor:'pointer', fontWeight:'bold'}}>Close Preview</button>
                    </div>
                </div>
            )}
        </>
    );
};

export default Inductions;